import type { PageContext } from '../lib/types';
// Home (IA §51, seed §39): a working dashboard, not a catalog.
import { html } from '../lib/dom';
import * as store from '../store/ui';
import { home } from '../api/search';
import { createProject, getProject, projectBrands, getWorkspace, currentUser, projectStats } from '../api/workspace';
import { flowGraph, getFlow, projectFlows } from '../api/flows';
import { runCounts } from '../api/runs';
import { ownerLabel } from '../api/library';
import { projectCard, assetCard, runRow, sectionHead, status, empty, typeTag } from '../components/ui';
import { coverThumb, assetThumb } from '../components/thumb';
import { icon } from '../components/icons';
import { elapsedDaysHours } from '../lib/format';
import { createElement, lazy, Suspense } from 'react';
import { usesDesignV2 } from '../features/design-v2/rollout';
import { openNewProject } from './projects';
import { AddAssetModal } from '../features/design-v2/AddAssetModal';
import { createBlankFlow, createNodeStarterGraph, importFlowGraphText } from '../api/flow-directory';
import { flowTemplates } from '../../../../packages/canvas-runtime/flow-templates.mjs';
import { currentUser as getCurrentUser } from '../api/workspace';

const DashboardPage = lazy(() => import('../features/design-v2/DashboardPage').then(module => ({ default: module.DashboardPage })));

const favHref = (f?: any) => ({
  project: `#/projects/${f.entity_id}`,
  flow: `#/projects/${f.entity.project_id}/flows/${f.entity_id}`,
  asset: `#/assets/${f.entity_id}`,
  brand: `#/brands/${f.entity_id}`,
} as Record<string, string>)[f.entity_type];

function favThumb(f?: any) {
  if (f.entity_type === 'asset') return assetThumb(f.entity);
  return coverThumb(f.entity);
}

function continueCard({ flow, project, at, last }: any) {
  return html`<a class="card hover continue-card" href="#/projects/${project.id}/flows/${flow.id}">
    ${coverThumb(flow.cover?.asset_id ? flow : project, 'thumb continue-cover')}
    <div class="continue-body">
      <span class="muted ellipsis" title="${project.name}">${project.name}</span>
      <strong class="continue-title" title="${flow.name}">${flow.name}</strong>
      <div class="row wrap continue-meta">${last ? status(last.status) : ''}<span class="muted" data-opened-at="${at}">opened ${elapsedDaysHours(at)}</span></div>
      <span class="continue-action">Open canvas →</span>
    </div>
  </a>`;
}

const currentDateLabel = () => {
  const today = new Date();
  const weekday = today.toLocaleDateString('en-GB', { weekday: 'long' });
  const date = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  return `${weekday}, ${date}`;
};

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) return {
    title: 'Dashboard',
    content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading dashboard…') }, createElement(DashboardPage, {
      context: ctx,
      onCreateProject: (description = '') => openNewProject({ workspaceId: ctx.workspaceId, initialDescription: description, designV2:true }),
      onCreateBlankFlow: async (project: any) => {
        const flow = await createBlankFlow({ projectId: project.id, name: 'New Flow' });
        location.hash = `/projects/${project.id}/flows/${flow.id}`;
      },
      onImportGraph: async (project: any, file: File) => {
        if (file.size > 10 * 1024 * 1024) throw new Error('Graph files must be smaller than 10 MB.');
        const graph = await importFlowGraphText(await file.text());
        const name = file.name.replace(/(?:\.bnb)?\.json$/i, '').trim() || 'Imported Flow';
        const flow = await createBlankFlow({ projectId: project.id, name, description: `Imported from ${file.name}`, graph });
        location.hash = `/projects/${project.id}/flows/${flow.id}`;
      },
      onDuplicateProject: async (project: any) => {
        const brandId = projectBrands(project.id)[0]?.id || null;
        const id = await createProject({ name: `${project.name} copy`, workspaceId: project.workspace_id, brandId, description: project.description || '', userId: currentUser().id });
        try {
          for (const flow of projectFlows(project.id)) {
            const graph = flowGraph(flow.id).draft?.graph || flow.graph;
            await createBlankFlow({ projectId:id, name:flow.name, description:flow.description || '', graph });
          }
        } catch (cause) {
          location.hash = `/projects/${id}?design=v2`;
          throw new Error(`Project was created, but not all flows could be copied: ${cause instanceof Error ? cause.message : 'unknown error'}`);
        }
        location.hash = `/projects/${id}?design=v2`;
      },
      onUseTemplate: async (project: any, template: any) => {
        const flow = await createBlankFlow({ projectId: project.id, name: template.name, templateId: template.id });
        location.hash = `/projects/${project.id}/flows/${flow.id}`;
      },
      onCreateNodeFlow: async (project: any, type: string) => {
        const starter = await createNodeStarterGraph(type);
        const flow = await createBlankFlow({ projectId: project.id, name: starter.name, description: `Started with ${starter.name}`, graph: starter.graph });
        location.hash = `/projects/${project.id}/flows/${flow.id}`;
      },
      user: getCurrentUser(),
      templates: flowTemplates,
      AddAssetModal,
    })),
  };
  const s = store.get();
  const d = home({ recents: s.recents, favorites: s.favorites, workspaceId: ctx.workspaceId });
  const ws = ctx.workspaceId ? getWorkspace(ctx.workspaceId) : null;
  const counts = runCounts({ userId: currentUser().id, workspaceId: ctx.workspaceId || undefined });
  const recentRuns = d.recentRuns.slice(0, 5);
  const runCtx = (r?: any) => ({ flow: getFlow(r.flow_id), project: getProject(r.project_id) });

  const body = html`<div class="page">
    <header class="page-head">
      <div><div class="eyebrow">${ws ? html`${ws.name} · ` : ''}<span data-home-date>${currentDateLabel()}</span></div><h1>Good afternoon, ${currentUser().name}</h1></div>
      <div class="row"><a class="btn" href="#/assets">${icon('library')}Assets</a><a class="btn primary" href="#/projects?new=1">${icon('plus')}New project</a></div>
    </header>

    <section class="section">
      ${sectionHead('Your activity')}
      <div class="grid c3">
        <div class="card pad"><span class="muted">Your executions</span><h2>${d.statistics.runs}</h2></div>
        <div class="card pad"><span class="muted">Completed</span><h2>${d.statistics.completed}</h2></div>
        <div class="card pad"><span class="muted">Failed</span><h2>${d.statistics.failed}</h2></div>
      </div>
      <div class="row wrap activity-statuses">${['queued', 'running', 'cancelled'].map(key => html`<a class="chip" href="#/executions?user=${currentUser().id}&status=${key}${ctx.workspaceId ? '&workspace=' + ctx.workspaceId : ''}">${status(key)}<strong>${counts[key] || 0}</strong></a>`)}</div>
    </section>
    <section class="section">
      ${sectionHead('Continue working')}
      ${d.continueWorking.length ? html`<div class="grid c3 continue-grid">${d.continueWorking.map((item: any) => continueCard({ ...item, last: d.recentRuns.find(run => run.flow_id === item.flow.id) }))}</div>` : empty('Open a flow and it will show up here.')}
    </section>

    <div class="split">
      <div class="col" style="gap:28px">
        <section class="section">
          ${sectionHead('Recent projects', '#/projects?view=recent')}
          ${d.recentProjects.length ? html`<div class="grid c2">${d.recentProjects.map((p?: any) => projectCard(p, { workspace: getWorkspace(p.workspace_id), brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div>` : empty('No recent projects in this workspace.')}
        </section>
        <section class="section">
          ${sectionHead('Recently used assets', '#/assets')}
          <div class="grid c4">${d.recentAssets.map((a?: any) => assetCard(a, ownerLabel(a)))}</div>
        </section>
      </div>

      <div class="col" style="gap:20px">
        <section class="card pad">
          <div class="section-head" style="margin-bottom:6px"><h2>Executions</h2><a class="link" href="#/executions?user=${currentUser().id}">All executions →</a></div>
          <div class="eyebrow" style="margin:10px 0 2px">Recent</div>
          ${recentRuns.length ? recentRuns.map((r?: any) => runRow(r, runCtx(r))) : empty('No executions yet. Your flow executions will appear here.')}
        </section>

        <section class="card pad">
          <div class="section-head" style="margin-bottom:6px"><h2>Favorites</h2></div>
          ${d.favorites.length ? d.favorites.map((f?: any) => html`<a class="list-row" href="${favHref(f)}">${favThumb(f)}<div class="col" style="gap:2px;flex:1;min-width:0"><span class="t ellipsis">${f.entity.name}</span><span class="s">${f.entity_type === 'asset' ? typeTag(f.entity.type) : f.entity_type}</span></div><span class="fav on">${icon('starFill')}</span></a>`) : empty('Star projects, flows or assets to pin them here.')}
        </section>
      </div>
    </div>
  </div>`;

  return {
    title: 'Dashboard', body,
    mount(root: HTMLElement) {
      const updateDate = () => {
        const label = root.querySelector('[data-home-date]');
        if (label) label.textContent = currentDateLabel();
        root.querySelectorAll<HTMLElement>('[data-opened-at]').forEach(item => {
          item.textContent = `opened ${elapsedDaysHours(item.dataset.openedAt || '')}`;
        });
      };
      updateDate();
      const timer = window.setInterval(updateDate, 60_000);
      document.addEventListener('visibilitychange', updateDate);
      return () => {
        window.clearInterval(timer);
        document.removeEventListener('visibilitychange', updateDate);
      };
    },
  };
}
